import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import useStore from "../../helpers/useStore";
import Controls from '../../atoms/Controls';
import { Event } from './Calendar.type';
import 'react-dates/lib/css/_datepicker.css';
import 'react-dates/initialize';
import moment from 'moment';
import { Moment } from 'moment';
import { CalendarContext } from '../../stores/Calendar/calendar.provider';
import { LeaveOfAbsenceDto } from '../../api-client';
import notificationStore from '../../stores/Notification/notificationStore';
import { useUserAbsence } from '../../stores/Calendar/calendar.mutations';
import { useApi } from '../../api/ApiProvider';
import { AbsencePeriod, AbsenceSplitApi, applyAbsenceSplit, planAbsenceSplit } from './absenceOverlap';

interface AbsenceDialogProps {
  open: boolean;
  event?: Event;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  leaveOfAbsence?: LeaveOfAbsenceDto;
  blockedDays?: Moment[];
  highlightDays?: Moment[];
  allowPastDates?: boolean;
  /** Already stored absences of the same employee; overlapping ones are split around the saved period. */
  existingAbsences?: AbsencePeriod[];
  /** Days (YYYY-MM-DD) with active appointments of the employee; used to warn before cancelling them. */
  appointmentDays?: string[];
  /** Only therapists have appointments that would have to be cancelled. */
  isTherapist?: boolean;
}

interface AbsenceFormDto {
  userId?: string;
  type: string;
  from: string;
  to: string;
  comment?: string;
}

const AbsenceDialog: React.FC<AbsenceDialogProps> = ({
  open,
  event,
  onClose,
  onConfirm,
  title,
  leaveOfAbsence,
  blockedDays,
  highlightDays,
  allowPastDates = false,
  existingAbsences,
  appointmentDays,
  isTherapist = true,
}) => {

  moment.locale('de');

  const initialFormValues: AbsenceFormDto = {
    userId: leaveOfAbsence?.user?.id,
    type: leaveOfAbsence?.loAType || "",
    from: event?.start || "",
    to: event?.end || "",
    comment: "",
  };

  const [formState, setFormState] = useState<AbsenceFormDto>(initialFormValues);

  const defaultStartDate = event?.start || leaveOfAbsence?.start;
  const defaultEndDate = event?.end || leaveOfAbsence?.end;

  const [startDate, setStartDate] = useState<Moment | null>(moment(defaultStartDate));
  const [endDate, setEndDate] = useState<Moment | null>(moment(defaultEndDate));

  const [loading, setLoading] = useState(false);
  const [isAppointmentWarningOpen, setAppointmentWarningOpen] = useState(false);
  const CalendarStore = useStore(CalendarContext);
  const { updateUserAbsence } = CalendarStore;
  const {mutateAsync: userAbsence} = useUserAbsence();
  const { scheduler } = useApi();

  const absenceOptions =[
    { id: "vacation", label: "Urlaub", value: "Urlaub" },
    { id: "sickness", label: "Krank", value: "Krank" },
    { id: "sickness_sick_note", label: "Krank mit AU", value: "Krank mit AU" },
    { id: "sickness_child", label: "Kindkrank", value: "Kindkrank" },
    { id: "sickness_child_sick_note", label: "Kindkrank mit AU", value: "Kindkrank mit AU" },
    { id: "training", label: "Fortbildung", value: "Fortbildung" },
    { id: "compensatory_leave", label: "Ü frei", value: "Ü frei" },
    { id: "remaining_vacation", label: "Resturlaub", value: "Resturlaub" },
    { id: "special_leave", label: "Sonderurlaub", value: "Sonderurlaub" },
    { id: "parental_leave", label: "Elternzeit", value: "Elternzeit" },
    { id: "exempted", label: "Freigestellt", value: "Freigestellt" },
    { id: "professional_ban", label: "Berufsverbot", value: "Berufsverbot" },
  ];

  // `appointmentDays` holds one entry per appointment, so its length is the appointment count.
  const affectedAppointments = useMemo(() => {
    if (!isTherapist || !appointmentDays?.length || !startDate || !endDate) {
      return { total: 0, dayCount: 0 };
    }
    const from = startDate.format("YYYY-MM-DD");
    const to = endDate.format("YYYY-MM-DD");
    const affectedDays = appointmentDays.filter((day) => day >= from && day <= to);
    return { total: affectedDays.length, dayCount: new Set(affectedDays).size };
  }, [appointmentDays, isTherapist, startDate, endDate]);

  // Days the edited absence gives back; the backend restores the appointments it cancelled there.
  const releasedDayCount = useMemo(() => {
    if (!event?.id || !event.start || !event.end || !startDate || !endDate) {
      return 0;
    }
    const originalEnd = moment(event.end);
    let count = 0;
    for (const day = moment(event.start); day.isSameOrBefore(originalEnd, "day"); day.add(1, "day")) {
      if (day.isBefore(startDate, "day") || day.isAfter(endDate, "day")) {
        count++;
      }
    }
    return count;
  }, [event?.id, event?.start, event?.end, startDate, endDate]);

  useEffect(() => {
    setStartDate(moment(leaveOfAbsence?.start))
    setEndDate(moment(leaveOfAbsence?.end ? leaveOfAbsence?.end : leaveOfAbsence?.start ))
  }, [leaveOfAbsence?.start, leaveOfAbsence?.end])

  const userId = leaveOfAbsence?.user?.id;

  const splitApi: AbsenceSplitApi = {
    create: async (absence) => {
      const created = await userAbsence({
        userId: userId!,
        leaveOfAbsence: {
          loAType: absence.loAType,
          comment: absence.comment,
          start: absence.start,
          end: absence.end,
        },
      });
      return created?.id;
    },
    update: async (absence) => {
      const updated = await updateUserAbsence(userId!, {
        id: absence.id,
        loAType: absence.loAType,
        comment: absence.comment,
        start: absence.start,
        // The update endpoint stores the end verbatim, so the inclusive last day needs one day added.
        end: moment(absence.end).add(1, "day").format("YYYY-MM-DD"),
      });
      if (!updated) {
        throw new Error("Bestehende Abwesenheit konnte nicht angepasst werden");
      }
    },
    remove: async (id) => {
      await scheduler.schedulerDeleteTimeSlot(id, false);
    },
  };

  const handleConfirmClick = () => {
    if (loading) {
      return;
    }
    if (affectedAppointments.total > 0) {
      setAppointmentWarningOpen(true);
      return;
    }
    handleAddAbsenceClick();
  };

  const handleAppointmentWarningConfirm = () => {
    setAppointmentWarningOpen(false);
    handleAddAbsenceClick();
  };

  const handleAddAbsenceClick = async () => {
    if (loading) {
      return;
    }
    // Only an event carrying a persisted absence id is an edit; anything else is a new absence.
    const isEdit = !!event?.id && event.id !== "0";
    const start = startDate!.format("YYYY-MM-DD");
    const end = endDate!.format("YYYY-MM-DD");
    const userAbsenceDto: LeaveOfAbsenceDto = {
      loAType: formState.type,
      start,
      // The dialog works with the inclusive last day of the absence. The create endpoint
      // turns that into the exclusive end it stores, the update endpoint stores it verbatim.
      end: isEdit ? endDate!.clone().add(1, "day").format("YYYY-MM-DD") : end,
      comment: formState.comment,
    };

    setLoading(true);
    let rollbackSplit: (() => Promise<void>) | undefined;
    try {
      // The saved period wins its own days only: overlapping absences are shortened or split
      // around it instead of blocking the save.
      rollbackSplit = await applyAbsenceSplit(
        planAbsenceSplit(existingAbsences ?? [], { start, end }, isEdit ? event?.id : undefined),
        splitApi
      );
    } catch (error) {
      setLoading(false);
      notificationStore.showMessage(
        "Bestehende Abwesenheiten konnten nicht angepasst werden. Es wurden keine Änderungen gespeichert.",
        "error",
        error
      );
      return;
    }

    try {
      if (isEdit) {
        userAbsenceDto.id = event?.id;
      }
      const timeSlot = isEdit
        ? await updateUserAbsence(userId!, userAbsenceDto)
        : await userAbsence({ userId: userId!, leaveOfAbsence: userAbsenceDto });
      if (!timeSlot) {
        throw new Error("Abwesenheit konnte nicht gespeichert werden");
      }
      notificationStore.showMessage(
        isEdit ? "Abwesenheit erfolgreich geändert" : "Abwesenheit erfolgreich hinzugefügt",
        "success"
      );
      onConfirm();
      onClose();
      return timeSlot;
    } catch (error) {
      try {
        await rollbackSplit();
        notificationStore.showMessage("Fehler beim Hinzufügen der Abwesenheit", "error");
      } catch (rollbackError) {
        notificationStore.showMessage(
          "Fehler beim Hinzufügen der Abwesenheit. Die bestehenden Abwesenheiten konnten nicht wiederhergestellt werden – bitte prüfen Sie den Kalender.",
          "error",
          rollbackError
        );
      }
    } finally {
      setLoading(false);
    }
  }

  const handleInputChange = (fieldName: string, value: any) => {
    setFormState((prevFormState) => ({
      ...prevFormState,
      [fieldName]: value,
    }));
  };

  const handleDatesChange = ({ startDate, endDate }: { startDate: Moment | null, endDate: Moment | null }) => {
    setStartDate(startDate?.set({hour: 0, minute: 0, second: 0, millisecond: 0})!);
    setEndDate(endDate?.set({hour: 0, minute: 0, second: 0, millisecond: 0})!);
    // console.log('startDate in range picker', startDate);
    // console.log('endDate in range picker', endDate);

    handleInputChange("from", startDate?.format("YYYY-MM-DD"));
    handleInputChange("to", endDate?.format("YYYY-MM-DD"));
  };

  const isOutsideRange = (day: Moment) => {
    if (allowPastDates) return false;
    return day.isBefore(moment(), 'day');
  };

  return (
    <Dialog open={open!} onClose={onClose}>
      <DialogTitle id="dialog-title">{title}</DialogTitle>
      <DialogContent>
        <Grid container>
          <Grid item xs={12} marginBottom={2}>
            <Controls.Select
              placeholder="Bitte auswählen"
              label="Abwesenheitsart"
              name="type"
              value={
                absenceOptions.find((option) => option.label === formState.type)?.value ||
                initialFormValues.type
              }
              showOptionLabel
              options={absenceOptions}
              onChange={(e: any) => {
                const selected = absenceOptions.find((opt) => opt.id === e.target.id);
                handleInputChange("type", e.target.id);
                if (selected) {
                  const autoComment = `Therapeut ${selected.label}`;

                  handleInputChange("comment", autoComment);
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Controls.DateRangePicker
              startDate={startDate}
              endDate={endDate}
              handleDatesChange={handleDatesChange}
              isOutsideRange={isOutsideRange}
              displayFormat="DD/MM/YYYY"
              blockedDays={blockedDays!}
              highlightDays={highlightDays!}
              block
            />
          </Grid>
          <Grid item xs={12}>
            <Controls.TextArea
              label="ggf. Grund der Stornierung"
              name="comment"
              rows={5}
              value={formState.comment}
              onChange={(e: any) => handleInputChange("comment", e.target.value)}
            />
          </Grid>
          {releasedDayCount > 0 && (
            <Grid item xs={12}>
              <DialogContentText sx={{ mt: 1 }}>
                {releasedDayCount === 1
                  ? "Ein Tag fällt aus der Abwesenheit heraus."
                  : `${releasedDayCount} Tage fallen aus der Abwesenheit heraus.`}{" "}
                Die dort abgesagten Termine werden wiederhergestellt, sofern ihre ursprüngliche Zeit
                noch frei ist.
              </DialogContentText>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Abbrechen
        </Button>
        <Button onClick={handleConfirmClick} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Bestätigen"}
        </Button>
      </DialogActions>
      <Dialog open={isAppointmentWarningOpen} onClose={() => setAppointmentWarningOpen(false)}>
        <DialogTitle>Termine im Abwesenheitszeitraum</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Im gewählten Abwesenheitszeitraum ({startDate?.format("DD.MM.YYYY")}
            {endDate && !endDate.isSame(startDate, "day") && ` – ${endDate.format("DD.MM.YYYY")}`})
            liegen {affectedAppointments.total}{" "}
            {affectedAppointments.total === 1 ? "Termin" : "Termine"} an{" "}
            {affectedAppointments.dayCount} {affectedAppointments.dayCount === 1 ? "Tag" : "Tagen"}.
            Wenn Sie fortfahren, werden diese Termine storniert und in den bestehenden
            Stornierungs- bzw. Umbuchungs-Workflow aufgenommen.
          </DialogContentText>
          {releasedDayCount > 0 && (
            <DialogContentText sx={{ mt: 2 }}>
              Termine an den {releasedDayCount === 1 ? "herausfallenden Tag" : "herausfallenden Tagen"}{" "}
              werden wiederhergestellt, sofern ihre ursprüngliche Zeit noch frei ist.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppointmentWarningOpen(false)} variant="text">
            Abbrechen
          </Button>
          <Button onClick={handleAppointmentWarningConfirm}>Fortfahren</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AbsenceDialog